"use client";

import { useQueries, useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  scopeParams,
  useItemsScope,
  type ItemsScope,
} from "@/features/items/hooks/useItemsScope";
import { useProjectConfigs } from "@/features/project/ProjectContext";
import { apiFetch } from "@/lib/api";
import { filterDefs, type FilterDef } from "./filterDefs";
import { excludesOwnSelection, filterParams } from "./filterParams";
import { useSelectedFilters } from "./useSelectedFilters";

/** Mirrors the backend's `Facet` union in `items.facets.ts`. */
export interface ValueFacet {
  field: string;
  type: "values";
  values: { value: string; count: number }[];
}

export interface RangeFacet {
  field: string;
  type: "range";
  min: string | null;
  max: string | null;
  valueType: "number" | "date" | null;
}

export type Facet = ValueFacet | RangeFacet;

interface FacetResponse {
  facets: Facet[];
}

/** A filter definition, every value it can take, and the counts that apply now. */
export interface FilterWithFacet {
  def: FilterDef;
  /** Counts under the current selection; a value absent here would yield none. */
  facet: Facet | undefined;
  /** Every value the field takes, unfiltered — the list to render. */
  all: Facet | undefined;
}

/**
 * One facet request, as react-query options.
 *
 * Built as data rather than as a hook so the per-filter variants below can go
 * through `useQueries`, which is the only way to run a list of queries whose
 * length depends on how many filters the user has touched.
 */
function facetQueryOptions(
  scope: ItemsScope,
  fields: string[],
  ranges: string[],
  filter: string[]
): UseQueryOptions<FacetResponse> {
  // Filter choices are counted over the whole collection under the current search
  // and selection — never the map viewport (as the legacy app did). The bounds
  // narrow the *results*, not the values a filter can take: a value the map is
  // currently hiding is still one the user can pick. So the bbox is dropped from
  // both the request and the key, and panning neither refetches the facets nor
  // moves a count.
  //
  // The scope's own `filter` is replaced by the caller's, and `isReady` gates the
  // query rather than describing it — none of the three belong in the key.
  const { filter: _scopeFilter, isReady, bbox: _bbox, projectId, collectionId, lang, ...rest } =
    scope;

  return {
    queryKey: [
      "facets",
      { ...rest, projectId, collectionId, lang, fields, ranges, filter },
    ],
    queryFn: ({ signal }) =>
      apiFetch<FacetResponse>(
        `/projects/${projectId}/collections/${collectionId}/items/facets`,
        {
          params: { ...scopeParams({ ...scope, bbox: null }, filter), fields, ranges },
          lang,
          signal,
        }
      ),
    enabled:
      !!projectId &&
      !!collectionId &&
      isReady &&
      (fields.length > 0 || ranges.length > 0),
    // Counts changing under the user is less jarring than the whole filter bar
    // disappearing while they change one of them.
    placeholderData: (previous: FacetResponse | undefined) => previous,
  };
}

function byField(response: FacetResponse | undefined): Map<string, Facet> {
  const map = new Map<string, Facet>();
  for (const facet of response?.facets ?? []) map.set(facet.field, facet);
  return map;
}

/**
 * The filters to show, and the counts to show against them.
 *
 * Two queries at minimum, deliberately:
 *
 *  - **all** — facetted with no selection, so a checkbox list can show every
 *    value the field takes. Without it, unselecting would become impossible:
 *    once a value is the only one selected it's the only one still counted.
 *  - **applied** — facetted with the whole selection, giving live counts. A
 *    value missing here is one that would yield nothing, and renders disabled
 *    rather than disappearing.
 *
 * Under `or` logic a filter's counts leave out its *own* selection — the user is
 * choosing among its values, so selecting "museum" must not zero the others —
 * while still applying every other filter. That can't come from a shared query,
 * so each such filter gets one of its own.
 */
export function useFilters(): {
  filters: FilterWithFacet[];
  isPending: boolean;
} {
  const configs = useProjectConfigs();
  const { selectedFilters } = useSelectedFilters();
  const scope = useItemsScope();

  const defs = useMemo(() => filterDefs(configs), [configs]);
  const fields = useMemo(
    () => defs.filter((def) => def.kind === "simple").map((def) => def.field),
    [defs]
  );
  const ranges = useMemo(
    () => defs.filter((def) => def.kind !== "simple").map((def) => def.field),
    [defs]
  );

  const all = useQuery(facetQueryOptions(scope, fields, ranges, []));
  const applied = useQuery(facetQueryOptions(scope, fields, ranges, scope.filter));

  // Only filters the user has actually selected in need their own query, and
  // only when the project's logic is `or`.
  const ownScoped = useMemo(
    () =>
      excludesOwnSelection(configs.filterLogic)
        ? defs.filter((def) => selectedFilters[def.name]?.length)
        : [],
    [configs.filterLogic, defs, selectedFilters]
  );

  const ownQueries = useQueries({
    queries: ownScoped.map((def) =>
      facetQueryOptions(
        scope,
        def.kind === "simple" ? [def.field] : [],
        def.kind === "simple" ? [] : [def.field],
        filterParams(selectedFilters, defs, def.name)
      )
    ),
  });

  const appliedByField = useMemo(() => byField(applied.data), [applied.data]);
  const allByField = useMemo(() => byField(all.data), [all.data]);
  const ownByField = useMemo(() => {
    const map = new Map<string, Facet>();
    for (const query of ownQueries) {
      for (const facet of query.data?.facets ?? []) map.set(facet.field, facet);
    }
    return map;
  }, [ownQueries]);

  return {
    filters: defs.map((def) => ({
      def,
      facet: ownByField.get(def.field) ?? appliedByField.get(def.field),
      all: allByField.get(def.field),
    })),
    isPending: all.isPending || applied.isPending,
  };
}
